import { logException } from "@wss/error-tracking";
import { z } from "zod";
import { SectionGroup } from "./Filters";

export const getNewFilterRangeUrl = (
  group: SectionGroup,
  pageUrl: string,
  min: string,
  max: string,
  unit: string | undefined,
) => {
  const groupItemsByUom = group.groupItems.filter(
    (x) => x.unitOfMeasurement === unit,
  );

  const filterOptionMinValue = !groupItemsByUom[0]?.isNumeric
    ? groupItemsByUom[0]?.rangeStartValue
    : groupItemsByUom[0]?.valueNamePart;

  const minValue = !min ? filterOptionMinValue : min;

  const lastIndex = groupItemsByUom.length - 1;

  const filterOptionMaxValue = !groupItemsByUom[lastIndex]?.isNumeric
    ? groupItemsByUom[lastIndex]?.rangeEndValue
    : groupItemsByUom[lastIndex]?.valueNamePart;
  const maxValue = !max ? filterOptionMaxValue : max;

  const queryParam = `${minValue}~${maxValue}_-${unit
    ?.toLocaleLowerCase()
    .replace(/ /g, "-")}`;

  const url = new URL(pageUrl, window.location.origin);

  const searchParams = Array.from(url.searchParams.entries());

  // Get the values of the query parms that have a key of filter
  const filterParams = searchParams
    .filter(([key]) => key === "filter")
    .map(([, value]) => value);

  url.searchParams.delete("filter");

  // Get list of values that does not include the filter from the group.groupNameEncoded
  const safeListForSpecificationGroup = filterParams.filter((param) => {
    return !param.includes(group.groupNameEncoded);
  });

  // Using the safeList to build back the url
  safeListForSpecificationGroup.forEach((param) =>
    url.searchParams.append("filter", param),
  );

  // Add the new filter group name and value to the url
  url.searchParams.append("filter", `${group.groupNameEncoded}:${queryParam}`);

  // Checking if the safeList values include a colon to know if specification filter group has multiple filters selected
  const checkingFilterIsMulti = safeListForSpecificationGroup.some((param) => {
    const value = param.slice(param.indexOf(":") + 1);
    return value.includes(":");
  });

  const vendorParams = searchParams
    .filter(([key]) => key === "vendor")
    .map(([, value]) => value);

  // Checking if the vendor values include a comma to know if the vendor filter group has multiple filters selected
  const checkingVendorIsMulti = vendorParams.some((param) => {
    const value = param?.slice(param.indexOf(","));
    return value?.includes(",");
  });

  const priceParams = searchParams
    .filter(([key]) => key === "price")
    .map(([, value]) => value);

  // Checking if the price values include a comma to know if price filter group has multiple filters selected
  const checkingPriceIsMulti = priceParams.some((param) => {
    const value = param?.slice(param.indexOf(",") + 1);
    return value?.includes(",");
  });

  if (
    !checkingFilterIsMulti &&
    !checkingVendorIsMulti &&
    !checkingPriceIsMulti
  ) {
    url.searchParams.delete("multi");
  }

  const pathname = url.pathname + url.search;

  return decodeURIComponent(pathname);
};

export const getProductsAvailableResponse = async (newUrl: {
  rawClientURl: string;
}) =>
  fetch("/api/productlisting/verifyproductlistinghasproducts", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(newUrl),
  });

export const unitValueRequiredMessage = "Unit is required";

export const filterRangeValidationSchema = (
  showSelect: boolean,
  defaultUnitValue = "",
  group: SectionGroup,
  pageUrl: string,
) => {
  const decimalInputLimitRegex = /^-?\d*[.]?\d{0,6}$/;
  const numberInputRegex = /^\s*$|^-?\d*[.]?\d+$/;

  const getGroupItemsByUom = (units: string) => {
    const getUnit = !units ? defaultUnitValue : units;
    return group.groupItems.filter((x) => x.unitOfMeasurement === getUnit);
  };

  const getFirstFilterRangeStartValue = (units: string) => {
    const groupItemsByUom = getGroupItemsByUom(units);
    return !groupItemsByUom[0]?.isNumeric
      ? groupItemsByUom[0]?.rangeStartValue
      : groupItemsByUom[0]?.valueNamePart;
  };

  const getLastFilterRangeEndValue = (units: string) => {
    const groupItemsByUom = getGroupItemsByUom(units);
    const lastIndex = groupItemsByUom.length - 1;
    return !groupItemsByUom[lastIndex]?.isNumeric
      ? groupItemsByUom[lastIndex]?.rangeEndValue
      : groupItemsByUom[lastIndex]?.valueNamePart;
  };

  const checkIfProductsAvailable = async (
    min: string,
    max: string,
    units: string,
  ) => {
    const newUrl = {
      rawClientURl: getNewFilterRangeUrl(
        group,
        pageUrl,
        min,
        max,
        !units ? defaultUnitValue : units,
      ),
    };
    try {
      const response = await getProductsAvailableResponse(newUrl);
      if (response.ok) {
        return await response.json<boolean>();
      }
    } catch (error) {
      logException(error);
    }
    return false;
  };

  return z
    .object({
      min: z.string().trim(),
      max: z.string().trim(),
      units: showSelect
        ? z
            .string({ message: unitValueRequiredMessage })
            .nonempty(unitValueRequiredMessage)
        : z
            .string()
            .optional()
            .transform((data) => data || ""),
    })
    .transform(({ min, max, units }) => ({
      min,
      max,
      units,
      firstFilterRangeStartValue: Number(getFirstFilterRangeStartValue(units)),
      lastFilterRangeEndValue: Number(getLastFilterRangeEndValue(units)),
    }))
    .superRefine(
      (
        { min, max, firstFilterRangeStartValue, lastFilterRangeEndValue },
        ctx,
      ) => {
        // Min or Max is required
        if (!min && !max) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min or Max is required",
            path: ["min"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min or Max is required",
            path: ["max"],
          });

          return;
        }

        const minInvalid = !numberInputRegex.test(min);
        const maxInvalid = !numberInputRegex.test(max);
        const minInvalidDecimals = !decimalInputLimitRegex.test(min);
        const maxInvalidDecimals = !decimalInputLimitRegex.test(max);
        const minIsNegative = Math.sign(Number(min)) === -1;
        const maxIsNegative = Math.sign(Number(max)) === -1;
        const minGreaterThanLast = min && Number(min) > lastFilterRangeEndValue;
        const maxLessThanFirst =
          max && Number(max) < firstFilterRangeStartValue;
        const minLessThanFirst =
          min && Number(min) <= firstFilterRangeStartValue;
        const maxGreaterThanLast =
          max && Number(max) >= lastFilterRangeEndValue;

        let hasMinError = false;
        let hasMaxError = false;

        // Min/Max character restrictions
        if (minInvalid && maxInvalid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min and Max do not allow for the characters applied",
            path: ["min"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min and Max do not allow for the characters applied",
            path: ["max"],
          });
          hasMinError = true;
          hasMaxError = true;
        } else if (minInvalid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min does not allow for the character applied",
            path: ["min"],
          });
          hasMinError = true;
        } else if (maxInvalid) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Max does not allow for the character applied",
            path: ["max"],
          });
          hasMaxError = true;
        }

        // Min/Max decimal limit
        if (
          !hasMinError &&
          !hasMaxError &&
          minInvalidDecimals &&
          maxInvalidDecimals
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min and Max must be limited to 6 decimal places",
            path: ["min"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min and Max must be limited to 6 decimal places",
            path: ["max"],
          });
          hasMinError = true;
          hasMaxError = true;
        } else if (!hasMinError && minInvalidDecimals) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min must be limited to 6 decimal places",
            path: ["min"],
          });
          hasMinError = true;
        } else if (!hasMaxError && maxInvalidDecimals) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Max must be limited to 6 decimal places",
            path: ["max"],
          });
          hasMaxError = true;
        }

        // Negative number checks
        if (!hasMinError && !hasMaxError && minIsNegative && maxIsNegative) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min and Max cannot be negative",
            path: ["min"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min and Max cannot be negative",
            path: ["max"],
          });
          hasMinError = true;
          hasMaxError = true;
        } else if (!hasMinError && minIsNegative) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Min cannot be negative",
            path: ["min"],
          });
          hasMinError = true;
        } else if (!hasMaxError && maxIsNegative) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Max cannot be negative",
            path: ["max"],
          });
          hasMaxError = true;
        }

        // Min Greather Than End Range
        if (!hasMinError && minGreaterThanLast) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Min value must be equal to or less than ${lastFilterRangeEndValue}`,
            path: ["min"],
          });
          hasMinError = true;
        }

        // Max Less Than Start Range
        if (!hasMaxError && maxLessThanFirst) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Max value must be equal to or greater than ${firstFilterRangeStartValue}`,
            path: ["min"],
          });
          hasMinError = true;
        }

        // @TODO: Potential Bug?
        // If MAX isn't set but MIN is set and it is less than or equal to firstFilterRangeStartValue you get an error
        // If Min isn't set but MAX is set and it is greater than or equal to lastFilterRangeEndValue you get an error
        // HOWEVER, if min or max is set and none of the previous validation caught then we let it through on Production
        // so I made sure these checks acted the same however this doesn't seem right IMO
        if (
          !hasMinError &&
          !hasMaxError &&
          minLessThanFirst &&
          maxGreaterThanLast
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Min value must be greater than ${firstFilterRangeStartValue}`,
            path: ["min"],
          });
          hasMinError = true;
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Max value must be less than ${lastFilterRangeEndValue}`,
            path: ["max"],
          });
          hasMaxError = true;
        } else if (!max && !hasMinError && minLessThanFirst) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Min value must be greater than ${firstFilterRangeStartValue}`,
            path: ["min"],
          });
          hasMinError = true;
        } else if (!min && !hasMaxError && maxGreaterThanLast) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Max value must be less than ${lastFilterRangeEndValue}`,
            path: ["max"],
          });
          hasMaxError = true;
        }

        // Stop further checks if any validation failed
        if (hasMaxError || hasMinError) return;

        // If both Min and Max are entered, enforce both checks
        if (min && max) {
          // Min must be less than Max
          if (Number(min) >= Number(max)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Min must be less than Max",
              path: ["min"],
            });
          }
        }
      },
    )
    .refine(
      async ({ min, max, units }) => {
        return checkIfProductsAvailable(min, max, units);
      },
      { message: "No products available in this range", path: ["custom"] },
    );
};

export const getShortenUnits = (units: string) => {
  switch (units.toLowerCase()) {
    case "inches":
      return "in.";
    case "gallons":
      return "gal.";
    case "pounds":
      return "lb.";
    case "fl oz.":
      return "fl oz";
    case "cu. ft.":
      return "cu ft";
    case "ounces":
      return "oz.";
    case "quarts":
      return "qt.";
    default:
      return units;
  }
};
